/**
 * Display URL for an organizer logo on /org/<slug>.
 *
 * The ingesters store whatever the upstream platform hands out, and neither
 * hands out something sized for a 128px tile:
 *
 * - Challengermode image URLs carry their size as a `_<w>_<h>` suffix and the
 *   org API returns the 64x64 variant, which looks soft when upscaled to the
 *   100-128 CSS px tile (200-256 device px on retina). The CDN also serves
 *   `_128_128` and `_256_256` (`_512_512` 404s), so ask for 256.
 * - Battlefy logos are raw Firebase Storage uploads, often 1-5k px PNGs of
 *   1-3 MB. Those are fixed by letting next/image resize them, not here.
 *
 * Rewriting at render time instead of in the table keeps `logo_url` (a SYNCED
 * column the ingester overwrites daily) untouched. Anything that isn't a
 * small Challengermode variant passes through unchanged.
 */
const CHALLENGERMODE_SIZE = /^(https:\/\/[a-z0-9-]+\.challengermode\.com\/[^?#]+)_(\d+)_(\d+)$/i
const CHALLENGERMODE_LOGO_PX = 256

export function orgLogoSrc(url: string | null | undefined): string | null {
  if (!url) return null
  const match = CHALLENGERMODE_SIZE.exec(url)
  if (!match) return url
  const [, base, w, h] = match
  if (Number(w) >= CHALLENGERMODE_LOGO_PX && Number(h) >= CHALLENGERMODE_LOGO_PX) return url
  return `${base}_${CHALLENGERMODE_LOGO_PX}_${CHALLENGERMODE_LOGO_PX}`
}

/**
 * Animated GIF logos. next/image bypasses its optimizer for animated images
 * and serves them as-is, so a Battlefy GIF goes out at full weight (one is a
 * 744 KB 480px GIF for a 128px tile). Those go through /api/org-logo instead,
 * which re-encodes them to a 256px animated WebP (~10x smaller) and keeps the
 * animation. Battlefy URLs keep the file extension in the encoded object path
 * (`...%2F1754089901931.gif?alt=media&token=...`).
 */
export const BATTLEFY_LOGO_PREFIX =
  'https://firebasestorage.googleapis.com/v0/b/battlefy-2f59d.appspot.com/o/'
export const ORG_LOGO_PROXY_PATH = '/api/org-logo'

export function isProxiedOrgLogo(url: string): boolean {
  try {
    // Parsed (dot segments resolved) so `/o/../..` can't leave the bucket.
    const u = new URL(url)
    return (
      `${u.origin}${u.pathname}`.startsWith(BATTLEFY_LOGO_PREFIX) &&
      u.pathname.toLowerCase().endsWith('.gif')
    )
  } catch {
    return false
  }
}

/** Logo tile URL: orgLogoSrc(), with animated GIFs routed through the proxy. */
export function orgLogoTileSrc(url: string | null | undefined): string | null {
  const src = orgLogoSrc(url)
  if (!src || !isProxiedOrgLogo(src)) return src
  return `${ORG_LOGO_PROXY_PATH}?src=${encodeURIComponent(src)}`
}
